import { Button } from "components/DesignSystem/Button/Button";
import { CheckboxField } from "components/DesignSystem/Checkbox/CheckboxField";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, FormikValues, useFormikContext } from "formik";
import { Person, PeopleResponse } from "types/Models/apAutofills";
import { PersonaAutofill } from "types/Models/personaAutofill";
import { Countries } from "types/Models/countries";
import { PersonaCombobox } from "./PersonaCombobox";
import { ConfirmReplaceSignatoryModal } from "./ConfirmReplaceSignatoryModal";
import { RolePillGroup } from "./RolePillGroup";
import { RoleAppointmentFields } from "./RoleAppointmentFields";
import { OtherRolesSection } from "./OtherRolesSection";
import { ShareholderRoleFields } from "./ShareholderRoleFields";
import {
  blankPersonFormValues,
  DIRECTOR_PILL,
  emptyRoleDetail,
  OTHER_PILL,
  PersonaTypeOption,
  SHAREHOLDER_PILL,
} from "./addPersonModal.utils";

type AddPersonFormBodyProps = {
  entityId: string;
  personToEdit?: Person | null;
  activePerson: Person | null;
  personaTypes: PersonaTypeOption[];
  countries?: Countries[];
  peopleData?: PeopleResponse;
  isEditMode: boolean;
  isShareholderLocked: boolean;
  isOtherPillLocked: boolean;
  selectedPersonName: string;
  showSignatoryWarning: boolean;
  onShowSignatoryWarningChange: (show: boolean) => void;
  onPersonaChange: (
    personaAutofillId: string,
    fallbackName?: string
  ) => typeof blankPersonFormValues;
  onClose: () => void;
};

export const AddPersonFormBody = ({
  entityId,
  personToEdit,
  activePerson,
  personaTypes,
  countries,
  peopleData,
  isEditMode,
  isShareholderLocked,
  isOtherPillLocked,
  selectedPersonName,
  showSignatoryWarning,
  onShowSignatoryWarningChange,
  onPersonaChange,
  onClose,
}: AddPersonFormBodyProps) => {
  const {
    values,
    setFieldValue,
    submitForm,
    isValid,
    isSubmitting,
    dirty,
    resetForm,
    validateForm,
  } = useFormikContext<FormikValues>();

  const togglePill = (pill: string) => {
    const isSelected = values.selectedPills.includes(pill);
    const next = isSelected
      ? values.selectedPills.filter((p: string) => p !== pill)
      : [...values.selectedPills, pill];
    setFieldValue("selectedPills", next);
    if (
      !isSelected &&
      pill !== SHAREHOLDER_PILL &&
      pill !== OTHER_PILL &&
      !values.roleDetails[pill]
    ) {
      setFieldValue(`roleDetails.${pill}`, emptyRoleDetail());
    }
  };

  const handlePersonaSelect = (persona: PersonaAutofill) => {
    const nextValues = onPersonaChange(
      persona.uuid,
      `${persona.first_name} ${persona.last_name}`.trim()
    );
    resetForm({ values: nextValues });
    void validateForm(nextValues);
  };

  const handlePersonaClear = () => {
    const nextValues = onPersonaChange("");
    resetForm({ values: nextValues });
    void validateForm(nextValues);
  };

  const canSubmit = isValid && (!isEditMode || dirty);

  const currentSignatory = [
    ...(peopleData?.people || []),
    ...(peopleData?.former_people || []),
  ].find(
    (person) =>
      person.is_authorised_signatory &&
      person.persona_autofill_id !== values.personaAutofillId
  );

  const handleSaveClick = () => {
    if (values.isAuthorisedSignatory && currentSignatory) {
      onShowSignatoryWarningChange(true);
      return;
    }
    submitForm();
  };

  const handleConfirmReplaceSignatory = () => {
    onShowSignatoryWarningChange(false);
    submitForm();
  };

  return (
    <>
      <Modal.Header>
        <Modal.Title className="!t-text-title-h2-bold">
          {isEditMode ? "Edit details" : "Add a person"}
        </Modal.Title>
        <Modal.Close onClick={onClose} />
      </Modal.Header>
      <Modal.Body>
        <Form className="t-m-0 t-flex t-flex-col t-gap-4">
          <div className="t-flex t-flex-col t-gap-2">
            <PersonaCombobox
              entityId={entityId}
              name="personaAutofillId"
              label="Select person"
              required
              selected={values.personaAutofillId}
              locked={Boolean(personToEdit)}
              lockedDisplayName={`${activePerson?.first_name || ""} ${
                activePerson?.last_name || ""
              }`.trim()}
              onSelect={handlePersonaSelect}
              onClear={handlePersonaClear}
            />

            <CheckboxField
              name="isAuthorisedSignatory"
              checkboxProps={{
                label: "This person is the authorized signatory",
              }}
            />
          </div>

          <div className="t-flex t-flex-col t-gap-4 t-mt-2">
            <div className="t-text-subtext t-text-text-60">
              Roles & appointment
            </div>
            <RolePillGroup
              personaTypes={personaTypes}
              selectedPills={values.selectedPills}
              roleDetails={values.roleDetails}
              isEditMode={isEditMode}
              isShareholderLocked={isShareholderLocked}
              isOtherPillLocked={isOtherPillLocked}
              onTogglePill={togglePill}
            />
          </div>

          {personaTypes
            .filter((type) => values.selectedPills.includes(type.uuid))
            .map((type) => (
              <div
                key={type.uuid}
                className="t-flex t-flex-col t-gap-3 t-rounded t-bg-surface-lighter-grey t-p-4"
              >
                <div className="t-text-subtext-sm t-text-text-60">
                  {type.name}
                </div>
                <RoleAppointmentFields
                  namePrefix={`roleDetails.${type.uuid}`}
                />
              </div>
            ))}

          {values.selectedPills.includes(DIRECTOR_PILL) && (
            <div className="t-flex t-flex-col t-gap-3 t-rounded t-bg-surface-lighter-grey t-p-4">
              <div className="t-text-subtext-sm t-text-text-60">Director</div>
              <RoleAppointmentFields
                namePrefix={`roleDetails.${DIRECTOR_PILL}`}
              />
            </div>
          )}

          {values.selectedPills.includes(SHAREHOLDER_PILL) && (
            <ShareholderRoleFields countries={countries} />
          )}

          {values.selectedPills.includes(OTHER_PILL) && (
            <OtherRolesSection isEditMode={isEditMode} />
          )}
        </Form>
      </Modal.Body>
      <Modal.FooterButtonGroup>
        <Modal.RawClose asChild>
          <Button type="button">Cancel</Button>
        </Modal.RawClose>
        <Button
          customType="primary"
          disabled={!canSubmit || isSubmitting}
          isLoading={isSubmitting}
          onClick={handleSaveClick}
        >
          {isEditMode ? "Save" : "Add"}
        </Button>
      </Modal.FooterButtonGroup>
      <ConfirmReplaceSignatoryModal
        open={showSignatoryWarning}
        onClose={() => onShowSignatoryWarningChange(false)}
        onConfirm={handleConfirmReplaceSignatory}
        currentSignatoryName={
          currentSignatory
            ? `${currentSignatory.first_name} ${currentSignatory.last_name}`.trim()
            : ""
        }
        newSignatoryName={selectedPersonName}
      />
    </>
  );
};
